// combatpro/panels/monsters/studio/MonsterStudioTabPanel.js
//
// Combat PRO - MonsterStudioTabPanel (Stage 56: Editor Runtime Foundation).
//
// The per-tab editor field renderer, ported from the legacy viStudioRenderActiveTab
// (battletracker.html 11048-11120) and the field builders vifText / vifNum /
// vifSelect / vifArea (11022-11034). Every field is a controlled input bound to
// the editor runtime: value comes from the working model, changes call the runtime
// handlers (setField / setCsv / card + spell handlers). No DOM, no innerHTML.
//
// Tab order, labels, placeholders, select options, and field paths are reproduced
// verbatim from the master spec. Select vocab (sizes / alignments / morale) and
// the card-list field map are consumed from the Stage 55 engine; the CSV display
// uses the engine toCsv helper. Card tabs delegate to MonsterStudioCards and the
// spellcasting tab to MonsterStudioSpellPicker.
//
// CSV fields (Tags / Collections) mirror the legacy onchange (commit-on-blur)
// behaviour: free typing in a local buffer, parsed into the model on blur, and
// resynced if the upstream value changes (e.g. New Monster). No optional chaining.
// No literal em dash.

import React, { useState } from 'react';
import {
  STUDIO_SIZES,
  STUDIO_ALIGNMENTS,
  STUDIO_MORALE,
  STUDIO_CARD_LISTS,
  toCsv
} from '../../../monster/monsterStudioEngine';
import MonsterStudioCards from './MonsterStudioCards';
import MonsterStudioSpellPicker from './MonsterStudioSpellPicker';

// ---- Controlled field helpers (vifText / vifNum / vifSelect / vifArea) ------

function StudioText(props) {
  var v = props.value == null ? '' : props.value;
  return (
    <div className="vi-st-field">
      <label>{props.label}</label>
      <input
        type="text"
        value={v}
        readOnly={props.readOnly ? true : false}
        placeholder={props.placeholder || ''}
        onChange={function(e) { props.onField(props.path, e.target.value); }}
      />
    </div>
  );
}

function StudioNum(props) {
  var v = props.value == null ? '' : props.value;
  return (
    <div className="vi-st-field">
      <label>{props.label}</label>
      <input
        type="number"
        value={v}
        onChange={function(e) { props.onField(props.path, e.target.value); }}
      />
    </div>
  );
}

function StudioSelect(props) {
  return (
    <div className="vi-st-field">
      <label>{props.label}</label>
      <select value={props.value == null ? '' : props.value} onChange={function(e) { props.onField(props.path, e.target.value); }}>
        {props.options.map(function(o) { return <option key={o} value={o}>{o}</option>; })}
      </select>
    </div>
  );
}

function StudioArea(props) {
  var v = props.value == null ? '' : props.value;
  return (
    <div className="vi-st-field vi-st-field-full">
      <label>{props.label}</label>
      <textarea rows={props.rows || 3} value={v} onChange={function(e) { props.onField(props.path, e.target.value); }} />
    </div>
  );
}

// Tags / Collections: free typing in a local buffer, commit (parse) on blur,
// resync when the upstream array changes.
function CsvField(props) {
  var initial = toCsv(props.value);
  const [text, setText] = useState(initial);
  const [prev, setPrev] = useState(initial);
  if (initial !== prev) { setPrev(initial); setText(initial); }
  return (
    <div className="vi-st-field vi-st-field-full">
      <label>{props.label}</label>
      <input
        type="text"
        value={text}
        placeholder={props.placeholder || ''}
        onChange={function(e) { setText(e.target.value); }}
        onBlur={function() { props.onCommit(text); }}
      />
    </div>
  );
}

var ABILITIES = ['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA'];

// ---- Tab body --------------------------------------------------------------

function TabBody(props) {
  var s = props.model;
  var tab = props.activeTab;
  var setField = props.setField;

  if (tab === 'identity') {
    return (
      <>
        <div className="vi-st-grid">
          <StudioText label="Name (display)" path="name" value={s.name} placeholder="Internal name" onField={setField} />
          <StudioText label="Custom Name" path="customName" value={s.customName} placeholder="e.g. Shadow Dragon Lord" onField={setField} />
          <StudioSelect label="Size" path="size" value={s.size} options={STUDIO_SIZES} onField={setField} />
          <StudioText label="Type" path="type" value={s.type} placeholder="e.g. Dragon" onField={setField} />
          <StudioSelect label="Alignment" path="alignment" value={s.alignment} options={STUDIO_ALIGNMENTS} onField={setField} />
          <StudioText label="CR" path="cr" value={s.cr} placeholder="e.g. 14" onField={setField} />
          <StudioText label="XP" path="xp" value={s.xp} placeholder="e.g. 11500" onField={setField} />
          <StudioText label="Race" path="race" value={s.race} placeholder="" onField={setField} />
          <StudioText label="Languages" path="language" value={s.language} placeholder="Common, Draconic" onField={setField} />
        </div>
        <div className="vi-st-grid" style={{ marginTop: '14px' }}>
          <CsvField label="Tags (comma separated)" value={s.tags} placeholder="Boss, Elite, Flying, Undead, Campaign1" onCommit={function(t) { props.setCsv('tags', t); }} />
          <CsvField label="Collections (comma separated)" value={s.collections} placeholder="Campaign Monsters, Boss Monsters, Session 8" onCommit={function(t) { props.setCsv('collections', t); }} />
        </div>
        <div className="vi-st-idline">Permanent ID: <b>{s.customMonsterId || '(assigned on first save)'}</b></div>
      </>
    );
  }

  if (tab === 'core') {
    return (
      <>
        <div className="vi-st-abil">
          {ABILITIES.map(function(k) {
            return (
              <div className="vi-st-abilbox" key={k}>
                <label>{k}</label>
                <input type="number" value={s.abilityScores[k]} onChange={function(e) { setField('abilityScores.' + k, e.target.value); }} />
              </div>
            );
          })}
        </div>
        <div className="vi-st-grid">
          <StudioText label="Saving Throws" path="saves" value={s.saves} placeholder="e.g. DEX +7, CON +9" onField={setField} />
          <StudioText label="Skills" path="skills" value={s.skills} placeholder="e.g. Perception +9, Stealth +7" onField={setField} />
          <StudioText label="Initiative Bonus" path="initiativeBonus" value={s.initiativeBonus} placeholder="e.g. +2" onField={setField} />
          <StudioText label="Passive Perception" path="passivePerception" value={s.passivePerception} placeholder="e.g. 19" onField={setField} />
        </div>
      </>
    );
  }

  if (tab === 'defences') {
    return (
      <div className="vi-st-grid">
        <StudioText label="AC" path="ac" value={s.ac} placeholder="e.g. 19 (natural armour)" onField={setField} />
        <StudioText label="HP" path="hpRange" value={s.hpRange} placeholder="e.g. 195 (17d12 + 85)" onField={setField} />
        <StudioText label="Hit Dice" path="hitDice" value={s.hitDice} placeholder="e.g. 17d12 + 85" onField={setField} />
        <StudioText label="Resistances" path="resistances" value={s.resistances} placeholder="e.g. Cold, Lightning" onField={setField} />
        <StudioText label="Immunities" path="immunities" value={s.immunities} placeholder="e.g. Fire, Poison" onField={setField} />
        <StudioText label="Vulnerabilities" path="vulnerabilities" value={s.vulnerabilities} placeholder="e.g. Radiant" onField={setField} />
        <StudioText label="Condition Immunities" path="conditionImmunities" value={s.conditionImmunities} placeholder="e.g. Charmed, Frightened" onField={setField} />
      </div>
    );
  }

  if (tab === 'movement') {
    return (
      <div className="vi-st-grid">
        <StudioText label="Walk" path="speed.walk" value={s.speed.walk} placeholder="30" onField={setField} />
        <StudioText label="Climb" path="speed.climb" value={s.speed.climb} placeholder="" onField={setField} />
        <StudioText label="Swim" path="speed.swim" value={s.speed.swim} placeholder="" onField={setField} />
        <StudioText label="Fly" path="speed.fly" value={s.speed.fly} placeholder="" onField={setField} />
        <StudioText label="Burrow" path="speed.burrow" value={s.speed.burrow} placeholder="" onField={setField} />
        <div className="vi-st-field">
          <label>Hover</label>
          <div className="vi-st-check">
            <input type="checkbox" checked={s.speed.hover ? true : false} onChange={function(e) { setField('speed.hover', e.target.checked); }} />
            <span>Can hover</span>
          </div>
        </div>
      </div>
    );
  }

  if (STUDIO_CARD_LISTS[tab]) {
    return (
      <MonsterStudioCards
        listName={STUDIO_CARD_LISTS[tab]}
        hasCost={tab === 'legendary'}
        cards={s[STUDIO_CARD_LISTS[tab]]}
        onAdd={props.addCard}
        onRemove={props.removeCard}
        onUpdate={props.updateCard}
      />
    );
  }

  if (tab === 'spellcasting') {
    return (
      <MonsterStudioSpellPicker spells={s.spells} onAdd={props.addSpell} onRemove={props.removeSpell} />
    );
  }

  if (tab === 'behaviour') {
    return (
      <>
        <div className="vi-st-grid">
          <StudioSelect label="Morale" path="behaviour.morale" value={s.behaviour.morale} options={STUDIO_MORALE} onField={setField} />
          <StudioText label="Leadership" path="behaviour.leadership" value={s.behaviour.leadership} placeholder="e.g. Pack leader, +1 morale" onField={setField} />
          <StudioText label="Retreat Threshold" path="behaviour.retreatThreshold" value={s.behaviour.retreatThreshold} placeholder="e.g. 25% HP" onField={setField} />
        </div>
        <StudioArea label="Combat Notes" path="behaviour.combatNotes" value={s.behaviour.combatNotes} rows={2} onField={setField} />
        <StudioArea label="Special AI Notes" path="behaviour.aiNotes" value={s.behaviour.aiNotes} rows={2} onField={setField} />
        <StudioArea label="Tactics" path="behaviour.tactics" value={s.behaviour.tactics} rows={3} onField={setField} />
      </>
    );
  }

  if (tab === 'conditions') {
    return (
      <>
        <StudioText label="Condition Immunities" path="conditionImmunities" value={s.conditionImmunities} placeholder="e.g. Charmed, Frightened, Poisoned" onField={setField} />
        <StudioArea label="Default Conditions" path="defaultConditions" value={s.defaultConditions} rows={2} onField={setField} />
        <StudioArea label="Special Effects" path="specialEffects" value={s.specialEffects} rows={3} onField={setField} />
      </>
    );
  }

  if (tab === 'notes') {
    return (
      <StudioArea label="Notes" path="notes" value={s.notes} rows={12} onField={setField} />
    );
  }

  return null;
}

function MonsterStudioTabPanel(props) {
  var editor = props.editor;
  return (
    <div className="vi-st-tabpanel" id="vi-st-tabpanel">
      <TabBody
        model={editor.model}
        activeTab={editor.activeTab}
        setField={editor.setField}
        setCsv={editor.setCsv}
        addCard={editor.addCard}
        removeCard={editor.removeCard}
        updateCard={editor.updateCard}
        addSpell={editor.addSpell}
        removeSpell={editor.removeSpell}
      />
    </div>
  );
}

export default MonsterStudioTabPanel;
